import { useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { toast, ToastContainer } from 'react-toastify';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { getCompilerVersions } from '@agnostico/browser-solidity-compiler';
import { Source } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto';
import { Connection } from '@solana/web3.js';
import { Navbar } from './Components/Navbar';
import { MonitorFeed } from './Components/pages/MonitorFeed';
import { Route, Routes, useLocation } from 'react-router-dom';
import { TokenCreate } from './Components/pages/TokenCreate/TokenCreate';
import { Header } from './Components/Header';
import { useBackend } from './Hooks/UseBackend';
import { Result } from './Components/pages/ResultPage';
import { Settings } from './Components/pages/Settings';
import { ErcTokenCreate } from './Components/pages/ErcTokenCreate';
import { Network, solididtyReleases } from './utils/types';
import Modal from './Components/Modal';
import { useSourceMaintainer } from './Hooks/sources/useSourceMaintainer';
import { rpcUrls } from './utils/rpc-urls';

import 'react-toastify/dist/ReactToastify.css';
import './styles.css';

function App() {
    const availableNetworks: Network[] = ['mainnet', 'fantom'];

    const [sources, setSources] = useState<Source[]>([]);
    const [visibleSources, setVisibleSources] = useState<Source[]>([]);
    const [isGeneralPostsVisible, setGeneralPostsVisible] = useState(true);
    const [isPumpFunsPostsVisible, setIsPumpFunsPostsVisible] = useState<boolean>(true);
    const [isBonkPostsVisible, setIsBonkPostsVisible] = useState<boolean>(true);
    const [sourceOrder, setSourceOrder] = useState<string[]>(() => {
        const savedOrder = localStorage.getItem('sourceOrder');
        return savedOrder ? JSON.parse(savedOrder) : [];
    });

    const [solidityReleases, setSolidityReleases] = useState<solididtyReleases>({});
    useEffect(() => {
        getCompilerVersions().then((response: any) => setSolidityReleases(response.releases));
    }, []);

    const location = useLocation();
    const locationWithoutHeader = ['/Settings', '/ResultPage', '/TokenCreate', '/ErcTokenCreate'].includes(location.pathname);

    const [solRpcUrl, setSolRpcUrl] = useState<string>(() => {
        const saved = localStorage.getItem('solRpcUrl');
        return saved ? saved : rpcUrls.sol;
    });

    const [solInitialBuyArrow, setSolInitialBuyArrow] = useState<number>(() => {
        const saved = localStorage.getItem('solInitialBuyArrow');
        return saved ? Number(saved) : 3;
    });

    const [solInitialBuyCopy, setSolInitialBuyCopy] = useState<number>(() => {
        const saved = localStorage.getItem('solInitialBuyCopy');
        return saved ? Number(saved) : 3;
    });

    const [ethLiquidityArrow, setEthLiquidityArrow] = useState<number>(() => {
        const saved = localStorage.getItem('ethLiquidityArrow');
        return saved ? Number(saved) : 3;
    });

    const [ethLiquidityCopy, setEthLiquidityCopy] = useState<number>(() => {
        const saved = localStorage.getItem('ethLiquidityCopy');
        return saved ? Number(saved) : 3;
    });

    const [solSnipeArrow, setSolSnipeArrow] = useState<number>(() => {
        const saved = localStorage.getItem('solSnipeArrow');
        return saved ? Number(saved) : 3;
    });

    const [solSnipeCopy, setSolSnipeCopy] = useState<number>(() => {
        const saved = localStorage.getItem('solSnipeCopy');
        return saved ? Number(saved) : 3;
    });

    const [ethSnipeArrow, setEthSnipeArrow] = useState<number>(() => {
        const saved = localStorage.getItem('ethSnipeArrow');
        return saved ? Number(saved) : 3;
    });

    const [ethSnipeCopy, setEthSnipeyCopy] = useState<number>(() => {
        const saved = localStorage.getItem('ethSnipeCopy');
        return saved ? Number(saved) : 3;
    });

    const [buyPrio, setBuyPrio] = useState<number>(() => {
        const saved = localStorage.getItem('buyPrio');
        return saved ? Number(saved) : 4000000;
    });

    const [sellPrio, setSellPrio] = useState<number>(() => {
        const saved = localStorage.getItem('sellPrio');
        return saved ? Number(saved) : 4000000;
    });

    const [autoSellDelayArrow, setAutoSellDelayArrow] = useState<number>(() => {
        const saved = localStorage.getItem('autoSellDelayArrow');
        return saved ? Number(saved) : 10;
    });

    const [autoSellProfitArrow, setAutoSellProfitArrow] = useState<number>(() => {
        const saved = localStorage.getItem('autoSellProfitArrow');
        return saved ? Number(saved) : 50;
    });

    const [autoSellDelayCreation, setAutoSellDelayCopy] = useState<number>(() => {
        const saved = localStorage.getItem('autoSellDelayCreation');
        return saved ? Number(saved) : 10;
    });

    const [autoSellProfitCreation, setAutoSellProfitCopy] = useState<number>(() => {
        const saved = localStorage.getItem('autoSellProfitCreation');
        return saved ? Number(saved) : 50;
    });

    const [ethPrivateKey, setEthPrivateKey] = useState<string>(() => {
        const saved = localStorage.getItem('ethPrivateKey');
        return saved ? saved : '';
    });

    const [solanaPrivateKey, setSolanaPrivateKey] = useState<string>(() => {
        const saved = localStorage.getItem('solanaPrivateKey');
        return saved ? saved : '';
    });

    const [solanaSniperPrivateKey, setSolanaSniperPrivateKey] = useState<string>(() => {
        const saved = localStorage.getItem('solanaSniperPrivateKey');
        return saved ? saved : '';
    });

    const [ethSniperPrivateKey, setEthSniperPrivateKey] = useState<string>(() => {
        const saved = localStorage.getItem('ethSniperPrivateKey');
        return saved ? saved : '';
    });

    const [zeroSlotBuyTip, setZeroSlotBuyTip] = useState<number>(() => {
        const saved = localStorage.getItem('zeroSlotBuyTip');
        return saved ? Number(saved) : 0.00011;
    });

    const [zeroSlotSellTip, setZeroSlotSellTip] = useState<number>(() => {
        const saved = localStorage.getItem('zeroSlotSellTip');
        return saved ? Number(saved) : 0.0001;
    });

    const { getSources } = useBackend();

    const [tweetTypes, setTweetTypes] = useState<{
        [key: string]: { selectedTweetTypes: Set<string>; soundTweetTypes: Set<string> };
    }>(() => {
        const initialTweetTypes: {
            [key: string]: { selectedTweetTypes: Set<string>; soundTweetTypes: Set<string> };
        } = {};

        sources.forEach((source) => {
            const selectedSaved = localStorage.getItem(`selectedTweetTypes_${source.username}`);
            const soundSaved = localStorage.getItem(`soundTweetTypes_${source.username}`);

            initialTweetTypes[source.username] = {
                selectedTweetTypes: selectedSaved ? new Set(JSON.parse(selectedSaved)) : new Set(['tweet', 'retweet', 'reply']),
                soundTweetTypes: soundSaved ? new Set(JSON.parse(soundSaved)) : new Set(['tweet', 'retweet', 'reply']),
            };
        });
        return initialTweetTypes;
    });

    const updateTweetTypes = (username: string, newSelectedTweetTypes: Set<string>, newSoundTweetTypes: Set<string>) => {
        setTweetTypes((prevTweetTypes) => ({
            ...prevTweetTypes,
            [username]: {
                selectedTweetTypes: newSelectedTweetTypes,
                soundTweetTypes: newSoundTweetTypes,
            },
        }));

        localStorage.setItem(`selectedTweetTypes_${username}`, JSON.stringify([...newSelectedTweetTypes]));
        localStorage.setItem(`soundTweetTypes_${username}`, JSON.stringify([...newSoundTweetTypes]));
    };

    useEffect(() => {
        getSources().then((data) => {
            setSources(data);

            let savedOrder = sourceOrder;
            if (sourceOrder.length === 0) {
                const storedOrder = localStorage.getItem('sourceOrder');
                savedOrder = storedOrder ? JSON.parse(storedOrder) : data.map((source) => source.username);
            }

            const updatedOrder = [...savedOrder];
            data.forEach((source) => {
                if (!updatedOrder.includes(source.username)) {
                    updatedOrder.push(source.username);
                }
            });

            setSourceOrder(updatedOrder);
            localStorage.setItem('sourceOrder', JSON.stringify(updatedOrder));

            setTweetTypes((prevTweetTypes) => {
                const newTweetTypes = { ...prevTweetTypes };

                data.forEach((source) => {
                    if (!newTweetTypes[source.username]) {
                        const selectedSaved = localStorage.getItem(`selectedTweetTypes_${source.username}`);
                        const soundSaved = localStorage.getItem(`soundTweetTypes_${source.username}`);

                        newTweetTypes[source.username] = {
                            selectedTweetTypes: selectedSaved ? new Set(JSON.parse(selectedSaved)) : new Set(['tweet', 'retweet', 'reply']),
                            soundTweetTypes: soundSaved ? new Set(JSON.parse(soundSaved)) : new Set(['tweet', 'retweet', 'reply']),
                        };
                    }
                });

                return newTweetTypes;
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleReorder = (draggedUsername: string, hoveredUsername: string) => {
        const draggedIndex = sourceOrder.indexOf(draggedUsername);
        const hoveredIndex = sourceOrder.indexOf(hoveredUsername);

        if (draggedIndex !== -1 && hoveredIndex !== -1) {
            const newOrder = [...sourceOrder];
            newOrder.splice(draggedIndex, 1);
            newOrder.splice(hoveredIndex, 0, draggedUsername);
            setSourceOrder(newOrder);

            localStorage.setItem('sourceOrder', JSON.stringify(newOrder));
        }
    };

    const handleToggleSource = (source: Source, checked: boolean) => {
        setVisibleSources((prev) => {
            const updatedSources = checked ? [...prev, source] : prev.filter((s) => s.username !== source.username);

            localStorage.setItem('visibleSources', JSON.stringify(updatedSources.map((s) => s.username)));
            return updatedSources;
        });
    };

    useEffect(() => {
        const storedUsernames = localStorage.getItem('visibleSources');
        if (storedUsernames) {
            const usernames = JSON.parse(storedUsernames) as string[];
            const restoredSources = sources.filter((s) => usernames.includes(s.username));
            setVisibleSources(restoredSources);
        } else {
            setVisibleSources(sources);
        }
    }, [sources]);

    const visibleGeneralPosts = (checked: boolean) => {
        setGeneralPostsVisible(checked);
        localStorage.setItem('isGeneralPostsVisible', JSON.stringify(checked));
    };

    useEffect(() => {
        const storedValue = localStorage.getItem('isGeneralPostsVisible');
        if (storedValue !== null) {
            setGeneralPostsVisible(JSON.parse(storedValue));
        }
    }, []);

    const visiblePumpFunsPosts = (checked: boolean) => {
        setIsPumpFunsPostsVisible(checked);
        localStorage.setItem('isPumpFunsPostsVisible', JSON.stringify(checked));
    };

    const visibleBonkPosts = (checked: boolean) => {
        setIsBonkPostsVisible(checked);
        localStorage.setItem('isBonkPostsVisible', JSON.stringify(checked));
    };

    useEffect(() => {
        const storedValue = localStorage.getItem('isPumpFunsPostsVisible');
        if (storedValue !== null) {
            setIsPumpFunsPostsVisible(JSON.parse(storedValue));
        }
    }, []);

    const [feedDisplayOpen, setFeedDisplayOpen] = useState(false);

    useSourceMaintainer({
        onRegisterSource: (source) => {
            try {
                const newSources = [...sources, source];
                setSources(newSources);
                console.log('Source maintenance started', source);
                toast.success(`Source maintenance started ${source.username}`);
            } catch (err) {
                console.error(err);
                toast.error(`Failed to handle Source Maintanance Started event`);
            }
        },
        onUnregisterSource: (source) => {
            try {
                console.log('Source maintenance stopped', source);
                const newSources = sources.filter((s) => s !== source);
                setSources(newSources);
                toast.error(`Source maintenance stopped ${source.username}`);
            } catch (err) {
                console.error(err);
                toast.error(`Failed to handle Source Maintanance Stopped event`);
            }
        },
    });

    const solConnection = useMemo(() => {
        return new Connection(solRpcUrl, 'processed');
    }, [solRpcUrl]);

    return (
        <DndProvider backend={HTML5Backend}>
            <div className='container'>
                <section className={`header ${locationWithoutHeader ? 'token-create-header' : ''}`}>
                    <Navbar
                        onFeedDisplayClick={() => {
                            setFeedDisplayOpen(!feedDisplayOpen);
                        }}
                    />
                </section>
                <section className='main-content'>
                    <Routes>
                        <Route
                            path='/'
                            element={
                                <MonitorFeed
                                    sources={visibleSources}
                                    isGeneralPostsVisible={isGeneralPostsVisible}
                                    isPumpFunsPostsVisible={isPumpFunsPostsVisible}
                                    isBonkPostsVisible={isBonkPostsVisible}
                                    tweetTypes={tweetTypes}
                                    updateTweetTypes={updateTweetTypes}
                                    sourceOrder={sourceOrder}
                                />
                            }
                        />
                        <Route
                            path='TokenCreate'
                            element={
                                <TokenCreate
                                    solInitialBuyArrow={solInitialBuyArrow}
                                    solInitialBuyCopy={solInitialBuyCopy}
                                    ethLiquidityArrow={ethLiquidityArrow}
                                    ethLiquidityCopy={ethLiquidityCopy}
                                    solSnipeArrow={solSnipeArrow}
                                    solSnipeCopy={solSnipeCopy}
                                    ethSnipeArrow={ethSnipeArrow}
                                    ethSnipeCopy={ethSnipeCopy}
                                    autoSellDelayArrow={autoSellDelayArrow}
                                    autoSellProfitArrow={autoSellProfitArrow}
                                    autoSellDelayCreation={autoSellDelayCreation}
                                    autoSellProfitCreation={autoSellProfitCreation}
                                    ethPrivateKey={ethPrivateKey}
                                    solPrivateKey={solanaPrivateKey}
                                    solSniperPrivateKey={solanaSniperPrivateKey}
                                    ethSniperPrivateKey={ethSniperPrivateKey}
                                    solidityReleases={solidityReleases}
                                    availableNetworks={availableNetworks}
                                    zeroSlotBuyTip={zeroSlotBuyTip}
                                    buyPrio={buyPrio}
                                    solConnection={solConnection}
                                />
                            }
                        />

                        <Route
                            path='Result'
                            element={
                                <Result
                                    solPrivateKey={solanaPrivateKey}
                                    zeroSlotSellTip={zeroSlotSellTip}
                                    solConnection={solConnection}
                                    solSniperPrivateKey={solanaSniperPrivateKey}
                                />
                            }
                        />
                        <Route
                            path='Settings'
                            element={
                                <Settings
                                    solInitialBuyArrow={solInitialBuyArrow}
                                    onSolInitialBuyArrowChange={(value) => {
                                        setSolInitialBuyArrow(value);
                                        localStorage.setItem('solInitialBuyArrow', value.toString());
                                    }}
                                    solInitialBuyCopy={solInitialBuyCopy}
                                    onSolInitialBuyCopyChange={(value) => {
                                        setSolInitialBuyCopy(value);
                                        localStorage.setItem('solInitialBuyCopy', value.toString());
                                    }}
                                    ethLiquidityArrow={ethLiquidityArrow}
                                    onEthLiquidityArrowChange={(value) => {
                                        setEthLiquidityArrow(value);
                                        localStorage.setItem('ethLiquidityArrow', value.toString());
                                    }}
                                    ethLiquidityCopy={ethLiquidityCopy}
                                    onEthLiquidityCopyChange={(value) => {
                                        setEthLiquidityCopy(value);
                                        localStorage.setItem('ethLiquidityCopy', value.toString());
                                    }}
                                    solSnipeArrow={solSnipeArrow}
                                    onSolSnipeArrowChange={(value) => {
                                        setSolSnipeArrow(value);
                                        localStorage.setItem('solSnipeArrow', value.toString());
                                    }}
                                    solSnipeCopy={solSnipeCopy}
                                    onSolSnipeCopyChange={(value) => {
                                        setSolSnipeCopy(value);
                                        localStorage.setItem('solSnipeCopy', value.toString());
                                    }}
                                    ethSnipeArrow={ethSnipeArrow}
                                    onEthSnipeArrowChange={(value) => {
                                        setEthSnipeArrow(value);
                                        localStorage.setItem('ethSnipeArrow', value.toString());
                                    }}
                                    ethSnipeCopy={ethSnipeCopy}
                                    onEthSnipeCopyChange={(value) => {
                                        setEthSnipeyCopy(value);
                                        localStorage.setItem('ethSnipeCopy', value.toString());
                                    }}
                                    solBuyPrio={buyPrio}
                                    onSolBuyPrioChange={(value) => {
                                        setBuyPrio(value);
                                        localStorage.setItem('buyPrio', value.toString());
                                    }}
                                    solSellPrio={sellPrio}
                                    onSolSellPrioChange={(value) => {
                                        setSellPrio(value);
                                        localStorage.setItem('sellPrio', value.toString());
                                    }}
                                    autoSellDelayArrow={autoSellDelayArrow}
                                    onAutoSellDelayArrowChange={(value) => {
                                        setAutoSellDelayArrow(value);
                                        localStorage.setItem('autoSellDelayArrow', value.toString());
                                    }}
                                    autoSellProfitArrow={autoSellProfitArrow}
                                    onAutoSellProfitArrowChange={(value) => {
                                        setAutoSellProfitArrow(value);
                                        localStorage.setItem('autoSellProfitArrow', value.toString());
                                    }}
                                    autoSellDelayCreation={autoSellDelayCreation}
                                    onAutoSellDelayCopyChange={(value) => {
                                        setAutoSellDelayCopy(value);
                                        localStorage.setItem('autoSellDelayCreation', value.toString());
                                    }}
                                    autoSellProfitCreation={autoSellProfitCreation}
                                    onAutoSellProfitCopyChange={(value) => {
                                        setAutoSellProfitCopy(value);
                                        localStorage.setItem('autoSellProfitCreation', value.toString());
                                    }}
                                    ethPrivateKey={ethPrivateKey}
                                    onEthPrivateKeyChange={(value) => {
                                        setEthPrivateKey(value);
                                        localStorage.setItem('ethPrivateKey', value.toString());
                                    }}
                                    solanaPrivateKey={solanaPrivateKey}
                                    onSolanaPrivateKeyChange={(value) => {
                                        setSolanaPrivateKey(value);
                                        localStorage.setItem('solanaPrivateKey', value.toString());
                                    }}
                                    solanaSniperPrivateKey={solanaSniperPrivateKey}
                                    onSolanaSniperPrivateKeyChange={(value) => {
                                        setSolanaSniperPrivateKey(value);
                                        localStorage.setItem('solanaSniperPrivateKey', value.toString());
                                    }}
                                    ethSniperPrivateKey={ethSniperPrivateKey}
                                    onEthSniperPrivateKeyChange={(value) => {
                                        setEthSniperPrivateKey(value);
                                        localStorage.setItem('ethSniperPrivateKey', value.toString());
                                    }}
                                    zeroSlotSellTip={zeroSlotSellTip}
                                    onZeroSlotSellTipChange={(value) => {
                                        setZeroSlotSellTip(value);
                                        localStorage.setItem('zeroSlotSellTip', value.toString());
                                    }}
                                    zeroSlotBuyTip={zeroSlotBuyTip}
                                    onZeroSlotBuyTipChange={(value) => {
                                        setZeroSlotBuyTip(value);
                                        localStorage.setItem('zeroSlotBuyTip', value.toString());
                                    }}
                                    solRpcUrl={solRpcUrl}
                                    onSolRpcUrlChange={(url) => {
                                        localStorage.setItem('solRpcUrl', url);
                                        setSolRpcUrl(url);
                                        toast.success(`SOL RPC URL is set to ${url}`);
                                    }}
                                />
                            }
                        />
                        <Route
                            path='ErcTokenCreate'
                            element={<ErcTokenCreate ethPrivateKey={ethPrivateKey} solidityReleases={solidityReleases} availableNetworks={availableNetworks} />}
                        />
                    </Routes>
                </section>
                <section className='toast-cont'>
                    <ToastContainer position='bottom-right' theme='dark' />
                </section>
            </div>
            <Modal header='Customize Feed' open={feedDisplayOpen} onClose={() => setFeedDisplayOpen(false)}>
                {!locationWithoutHeader && (
                    <div className='inputs'>
                        <div className='dyn-inputs'>
                            <label className={`custom-checkbox ${isGeneralPostsVisible ? 'checked' : ''}`}>
                                <span className='label-text'>Everyone</span>
                                <input type='checkbox' checked={isGeneralPostsVisible} onChange={(e) => visibleGeneralPosts(e.target.checked)} />
                                <span className='custom-checkbox-box'>
                                    <span className='checkmark'>✔</span>
                                    <span className='cross'>✖</span>
                                </span>
                            </label>
                        </div>
                        <div className='dyn-inputs '>
                            <label className={`custom-checkbox pumpfuns-header-main ${isBonkPostsVisible ? 'checked' : ''}`}>
                                <span className='label-text'>LetsBonk</span>
                                <input type='checkbox' checked={isBonkPostsVisible} onChange={(e) => visibleBonkPosts(e.target.checked)} />
                                <span className='custom-checkbox-box pumpfuns-custom-checkbox-box'>
                                    <span className='checkmark'>✔</span>
                                    <span className='cross'>✖</span>
                                </span>
                            </label>
                        </div>
                        <div className='dyn-inputs '>
                            <label className={`custom-checkbox pumpfuns-header-main ${isPumpFunsPostsVisible ? 'checked' : ''}`}>
                                <span className='label-text'>PumpFuns</span>
                                <input type='checkbox' checked={isPumpFunsPostsVisible} onChange={(e) => visiblePumpFunsPosts(e.target.checked)} />
                                <span className='custom-checkbox-box pumpfuns-custom-checkbox-box'>
                                    <span className='checkmark'>✔</span>
                                    <span className='cross'>✖</span>
                                </span>
                            </label>
                        </div>
                        {sourceOrder.map((username, index) => {
                            const source = sources.find((s) => s.username === username);
                            return (
                                source && (
                                    <Header
                                        key={index}
                                        source={source}
                                        onToggle={handleToggleSource}
                                        isChecked={visibleSources.some((s) => s.username === source.username)}
                                        sourceOrder={sourceOrder}
                                        handleReorder={handleReorder}
                                    />
                                )
                            );
                        })}
                    </div>
                )}
            </Modal>
        </DndProvider>
    );
}

export default App;
